import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TabActions, TabCard } from '@/components/layout/tab-card'
import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'

import { PlaceFeatureGroup, PlaceFeatureTab } from '../../../features/places-features.constants'
import { PlaceFeatureGroupSection } from '../../flags/place-feature-group'
import {
  PlaceFeatureGroupedList,
  PlaceFeaturesToggleAll,
} from '../../flags/place-feature-grouped-list'
import { usePlaceFlagsForm } from '../../flags/use-place-flags-form'
import { usePlaceOpenGroups } from '../../flags/use-place-open-groups'
import { PlaceFeaturesView } from '../../place-details.types'
import { AccessControlClassicLayout } from './access-control-classic-layout'

interface AccessControlTabProps {
  building: Building
  isAdmin: boolean
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
  view?: PlaceFeaturesView
}

export const ACCESS_CONTROL_GROUPS = [PlaceFeatureGroup.keyless, PlaceFeatureGroup.smartHome]

// Note: keyless has one root switch, so it is split into the root and one card per provider
export const ACCESS_CONTROL_BRANCH_GROUPS = [PlaceFeatureGroup.keyless]

export const AccessControlTab = ({
  building,
  isAdmin,
  onDirtyChange,
  view = PlaceFeaturesView.classic,
}: AccessControlTabProps) => {
  const { t } = useTranslation(['organisation'])
  const {
    errorFeatures,
    errorMessage,
    form,
    handleCancel,
    handleEdit,
    handleSave,
    isDirty,
    isEditing,
    isSaving,
  } = usePlaceFlagsForm({
    building,
    isAdmin,
    onDirtyChange,
    tab: PlaceFeatureTab.accessControl,
  })

  const { areAllOpen, openGroups, toggleAll, toggleGroup } =
    usePlaceOpenGroups(ACCESS_CONTROL_GROUPS)

  const isGrouped = view === PlaceFeaturesView.grouped

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <>
            {isGrouped && !isEditing && (
              <PlaceFeaturesToggleAll areAllOpen={areAllOpen} onClick={toggleAll} />
            )}
            <TabActions
              isDirty={isDirty}
              isEditing={isEditing}
              isSaving={isSaving}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onSave={handleSave}
            />
          </>
        }
        title={t('organisation:places.accessControl.tab.title')}
      >
        {isGrouped ? (
          <PlaceFeatureGroupedList
            branchGroups={ACCESS_CONTROL_BRANCH_GROUPS}
            building={building}
            errorFeatures={errorFeatures}
            errorMessage={errorMessage}
            groups={ACCESS_CONTROL_GROUPS}
            isAdmin={isAdmin}
            isEditing={isEditing}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
          />
        ) : (
          <AccessControlClassicLayout
            renderGroup={(group, splitPart, extraCards) => (
              <PlaceFeatureGroupSection
                showCount
                splitByBranch
                building={building}
                errorFeatures={errorFeatures}
                errorMessage={errorMessage}
                extraCards={extraCards}
                group={group}
                isAdmin={isAdmin}
                isEditing={isEditing}
                splitPart={splitPart}
              />
            )}
          />
        )}
      </TabCard>
    </FormProvider>
  )
}
